import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom"; // Ensure this is correctly imported from react-router-dom
import {
    Verified,
    Home,
    Wallet,
    MessageCircle,
    User,
    FileText,
    Shield,
    Bell,
    Search,
    Bookmark,
    LogOut,
    Settings,
    Lock,
    AlertCircle,
    Briefcase,
    FileCheck,
    Menu,
    X,
    ChevronRight,
    Globe,
    UserSearchIcon,
    User2,
    Settings2,
    ChartAreaIcon,
    StarIcon,
    DollarSign,
    Contact,
    Loader2,
    Award,
    TrendingUp,
    CheckCircle,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import PolicyModals from "./PolicyModals";
import SharedHeader4 from "../../Headers/SharedHeader4";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { MdReviews } from "react-icons/md";
import ProfessionalReviewsSection from "../../components/ui/ProfessionalReviewsSection";
import { GiProgression } from "react-icons/gi";
import { LogoutManually, LogoutUser, useAppDispatch, getProfessionalStats } from "../../redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import { useSelector } from "react-redux";
import SocialMediaLinks from "../../components/ui/SocialMediaLinks";
import { ContractStatusEnum } from "../../modules/shared/model/contract.enum";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { getPendingWarnings, dismissWarning, getMonitoringData } from "../../redux/Actions/ProfessionalMonitoringActions";
import WarningPopup from "../../components/monitoring/WarningPopup";
import { RootState } from "../../redux/store";

const InfoPopup = ({ isOpen, onClose, title, content }) => {
    if (!isOpen) return null;
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl max-w-lg w-full p-6 relative">
                <button onClick={onClose} className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full">
                    <X className="h-5 w-5" />
                </button>
                <h3 className="text-xl font-semibold mb-4">{title}</h3>
                <div className="prose max-w-none">{content}</div>
            </motion.div>
        </motion.div>
    );
};

const FreelancerDashboard = () => {
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [showHiring, setShowHiring] = useState(false);
    const [showQuotes, setShowQuotes] = useState(false);
    const [showReviews, setShowReviews] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showProfessionalTermsModal, setShowProfessionalTermsModal] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user, token } = useSelector((state: any) => state.auth);
    const { professionalStats } = useSelector((state: any) => state.professional);
    const isLoading = useSelector(selectLoaderValue);
    
    // Professional Monitoring Integration
    const [showWarning, setShowWarning] = useState(false);
    const [currentWarning, setCurrentWarning] = useState<any>(null);
    const { pendingWarnings, monitoringData } = useSelector((state: RootState) => state.professionalMonitoring);

    // Fetch professional stats on component mount
    useEffect(() => {
        if (user?.id) {
            dispatch(getProfessionalStats({ id: user.id, isShowLoader: false })).finally(() => {
                setIsDataLoading(false);
            });
            
            // Fetch monitoring data and check for warnings
            dispatch(getMonitoringData(user.id));
            dispatch(getPendingWarnings(user.id));
        }
    }, [user?.id, dispatch]);
    
    // Show warning popup if any pending warnings exist
    useEffect(() => {
        if (pendingWarnings && pendingWarnings.length > 0 && !showWarning) {
            setCurrentWarning(pendingWarnings[0]);
            setShowWarning(true);
        }
    }, [pendingWarnings]);

    // Check if data is loaded
    useEffect(() => {
        if (professionalStats && Object.keys(professionalStats).length > 0) {
            setIsDataLoading(false);
        }
    }, [professionalStats]);

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        } finally {
            navigate("/");
        }
    };

    const StoreLinks = () => (
        <div className="flex justify-center gap-2 pt-6 px-4">
            <a href="https://play.google.com/store/apps/details?id=com.kokobeo" className="w-1/2">
                <img
                    src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
                    alt="Get it on Google Play"
                    className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
                    style={{ maxWidth: "160px", height: "50px", objectFit: "contain" }}
                />
            </a>
            <a href="https://apps.apple.com/app/kokobeo" className="w-1/2">
                <img
                    src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
                    alt="Download on App Store"
                    className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
                    style={{ maxWidth: "160px", height: "50px", objectFit: "contain" }}
                />
            </a>
        </div>
    );

    const SocialIcons = () => (
        <SocialMediaLinks  className="flex justify-center space-x-4 pt-4 px-0 pb-6" iconSize="h-4 w-4" />
    );

    // Loading component - transparent overlay
    const LoadingSpinner = () => (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-lg flex items-center space-x-3">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-gray-700 font-medium">{t("professionalDashboard.loadingDashboard")}</span>
            </div>
        </div>
    );

    // Helper function to get status badge color
    const getStatusColor = (statusId: number) => {
        switch (statusId) {
            case ContractStatusEnum.Completed:
                return "bg-green-100 text-green-800";
            case ContractStatusEnum.Active:
            case ContractStatusEnum.Accepted:
                return "bg-blue-100 text-blue-800";
            case ContractStatusEnum.Pending:
                return "bg-yellow-100 text-yellow-800";
            case ContractStatusEnum.Cancelled:
            case ContractStatusEnum.Rejected:
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Helper function to format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric"
        });
    };

    // Helper function to calculate delivery time from milestone dueDate
    const calculateDeliveryTime = (milestones: any[]) => {
        if (!milestones || milestones.length === 0 || !milestones[0]?.dueDate) {
            return t("professionalDashboard.na");
        }

        const dueDate = new Date(milestones[0].dueDate);
        const now = new Date();
        const diffTime = dueDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return t("professionalDashboard.overdue");
        } else if (diffDays === 0) {
            return t("professionalDashboard.today");
        } else if (diffDays === 1) {
            return t("professionalDashboard.day", { count: 1 });
        } else if (diffDays < 7) {
            return t("professionalDashboard.days", { count: diffDays });
        } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return weeks === 1 ? t("professionalDashboard.week", { count: 1 }) : t("professionalDashboard.weeks", { count: weeks });
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return months === 1 ? t("professionalDashboard.month", { count: 1 }) : t("professionalDashboard.months", { count: months });
        } else {
            const years = Math.floor(diffDays / 365);
            return years === 1 ? t("professionalDashboard.year", { count: 1 }) : t("professionalDashboard.years", { count: years });
        }
    };

    const sidebarItems = [
        { icon: <Home className="h-5 w-5" />, label: t("sidebar.dashboard"), href: "/professionals", active: true, rightIcon: <Home className="h-5 w-5" /> },
        { icon: <UserSearchIcon className="h-5 w-5" />, label: t("sidebar.localCustomers"), href: "/professional" },
        {
            icon: <Bookmark className="h-5 w-5" />,
            label: t("sidebar.internationalClients"),
            href: "/international-professionals",
        },
        { icon: <User2 className="h-5 w-5" />, label: t("sidebar.myProfile"), href: "/myprofile" },
        { icon: <Settings2 className="h-5 w-5" />, label: t("sidebar.mySettings"), href: "/Settings" },
        { icon: <FileCheck className="h-5 w-5" />, label: t("sidebar.myOrders"), href: "/myorders" },
        { icon: <Bell className="h-5 w-5" />, label: t("sidebar.myOffers"), href: "/myoffers" },
        { icon: <FileText className="h-5 w-5" />, label: t("sidebar.myProposalsWhatsapp"), href: "/myproposals" },
        { icon: <StarIcon className="h-5 w-5" />, label: t("sidebar.favorites"), href: "/professional/favorites" },
        { icon: <ChartAreaIcon className="h-5 w-5" />, label: t("sidebar.progress"), href: "/Progress" },
        { icon: <FileCheck className="h-5 w-5" />, label: t("sidebar.subscriptions"), href: "/credits" },
        { icon: <MessageCircle className="h-5 w-5" />, label: t("sidebar.messageOrVideoCall"), href: "/MessageAndVideoCall" },
        { icon: <MdReviews className="h-5 w-5" />, label: t("sidebar.reviews"), href: "/professional/reviews" },
        { icon: <Wallet className="h-5 w-5" />, label: t("sidebar.wallet"), href: "/wallet" },
        { icon: <FileText className="h-5 w-5" />, label: t("sidebar.myInvoices"), href: "/invoices" },
        { icon: <FileText className="h-5 w-5" />, label: t("sidebar.withdrawHistory"), href: "/withdrawalhistroy" },
        { icon: <Wallet className="h-5 w-5" />, label: t("sidebar.refundRequest"), href: "/refund-requests" },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("sidebar.verifyAccount"),
            href: "/verify",
        },
        { icon: <Shield className="h-5 w-5" />, label: t("sidebar.supportTickets"), href: "/support" },
        { icon: <Contact className="h-5 w-5" />, label: t("sidebar.contactUs"), href: "/menu/contact" },
        // { icon: <Wallet className="h-5 w-5" />, label: t("sidebar.report"), href: "/client/reports" },
        
        { icon: <Lock className="h-5 w-5" />, label: t("sidebar.changePassword"), href: "/changepassword" },
        { icon: <AlertCircle className="h-5 w-5" />, label: t("sidebar.accountDelete"), href: "/DeleteAccount" },
        { icon: <LogOut className="h-5 w-5" />, label: t("sidebar.logOut"), href: "/login" },
    ];

    // Get stats from API data
    const stats = [
        {
            icon: <Wallet className="h-6 w-6 text-blue-600" />,
            title: t("professionalDashboard.walletBalance"),
            value: `${getDynamicCurrencySymbol()}${(professionalStats?.balance)?.toFixed(2) || "0.00"}`,
            bgColor: "bg-blue-50",
        },
        {
            icon: <Briefcase className="h-6 w-6 text-green-600" />,
            title: t("professionalDashboard.totalProjects"),
            value: professionalStats?.totalOffers?.toString() || "0",
            bgColor: "bg-green-50",
        },
        {
            icon: <FileCheck className="h-6 w-6 text-yellow-600" />,
            title: t("professionalDashboard.completeOrder"),
            value: professionalStats?.completedContractsCount?.toString() || "0",
            bgColor: "bg-yellow-50",
        },
        {
            icon: <AlertCircle className="h-6 w-6 text-purple-600" />,
            title: t("professionalDashboard.activeOrder"),
            value: professionalStats?.activeContractsCount?.toString() || "0",
            bgColor: "bg-purple-50",
        },
    ];

    // Get contracts (orders) from API data
    const orders = professionalStats?.contracts || [];

    // Get offers (projects) from API data
    const projects = professionalStats?.offers || [];


    // Loading skeleton for stats
    const StatsSkeleton = () => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
            {[...Array(4)].map((_, index) => (
                <Card key={index} className="p-6 animate-pulse">
                    <div className="flex items-center justify-between mb-4">
                        <div className="h-12 w-12 rounded-full bg-gray-200"></div>
                    </div>
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </Card>
            ))}
        </div>
    );

    // Loading skeleton for table
    const TableSkeleton = () => (
        <div className="overflow-x-auto w-full">
            <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-200 w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("professionalDashboard.orderId")}
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("professionalDashboard.budget")}
                            </th>
                            <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("professionalDashboard.deliveryTime")}
                            </th>
                            <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("professionalDashboard.status")}
                            </th>
                            <th className="hidden lg:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("professionalDashboard.date")}
                            </th>
                            <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t("professionalDashboard.action")}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {[...Array(3)].map((_, index) => (
                            <tr key={index} className="animate-pulse">
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                                </td>
                                <td className="hidden md:table-cell px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </td>
                                <td className="hidden sm:table-cell px-6 py-4">
                                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                                </td>
                                <td className="hidden lg:table-cell px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="h-8 bg-gray-200 rounded w-24 ml-auto"></div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
            {/* Fixed Header */}
            <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16 w-full">
                <SharedHeader4 />
            </div>

            <div className="flex flex-1 min-h-[calc(100vh-64px)] pt-16">
                {/* Desktop Sidebar - Fixed, Independent Scroll */}
                <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r overflow-y-auto h-[calc(100vh-64px)]">
                    <nav className="p-4 space-y-1 h-full flex flex-col">
                        <div className="flex-1 overflow-y-auto">
                            {sidebarItems.map((item, index) =>
                                item.href == "/login" ? (
                                    <button
                                        key={index}
                                        onClick={handleLogout}
                                        className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </button>
                                ) : item.rightIcon ? (
                                    <div key={index} className="flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                        <Link
                                            to={item.href}
                                            className={`flex items-center gap-3 ${item.active
                                                ? "text-blue-600 bg-blue-50 font-medium"
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            {item.icon}
                                            <span>{item.label}</span>
                                        </Link>
                                        <button
                                            onClick={() => navigate("/")}
                                            className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title="Go to Home"
                                        >
                                            <Home className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <Link
                                        key={index}
                                        to={item.href}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${item.active
                                            ? "text-blue-600 bg-blue-50 font-medium"
                                            : "text-gray-600 hover:bg-gray-50"
                                            }`}>
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </Link>
                                )
                            )}
                        </div>
                        <div className="mt-auto w-full">
                            <StoreLinks />
                            <SocialIcons />
                        </div>
                    </nav>
                </aside>

                {/* Mobile Sidebar - Slide-in, Independent Scroll, Full Width */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                            />
                            <motion.aside
                                initial={{ x: "-100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "-100%" }}
                                transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                                className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl z-50 lg:hidden flex flex-col"
                                style={{ top: "64px", height: "calc(100% - 64px)" }}>
                                <div className="flex items-center justify-between p-4 border-b">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src="https://assests.netlify.app/assets/images/logo.png"
                                            alt="Logo"
                                            className="h-8 w-8"
                                        />
                                        <span className="text-blue-600 text-lg font-bold">Kokobeo</span>
                                    </div>
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                        <X className="h-6 w-6" />
                                    </button>
                                </div>

                                {/* Scrollable menu items */}
                                <div className="flex-1 overflow-y-auto">
                                    <nav className="p-4">
                                        {sidebarItems.map((item, index) =>
                                            item.rightIcon ? (
                                                <div key={index} className="flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-colors mb-1 text-gray-600 hover:bg-gray-50">
                                                    <Link
                                                        to={item.href}
                                                        className={`flex items-center gap-3 ${item.active
                                                            ? "text-blue-600 bg-blue-50 font-medium"
                                                            : "text-gray-600 hover:bg-gray-50"
                                                            }`}
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        {item.icon}
                                                        <span>{item.label}</span>
                                                    </Link>
                                                    <button
                                                        onClick={() => {
                                                            navigate("/");
                                                            setIsMobileMenuOpen(false);
                                                        }}
                                                        className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                        title="Go to Home"
                                                    >
                                                        <Home className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <Link
                                                    key={index}
                                                    to={item.href}
                                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors mb-1 ${item.active
                                                        ? "text-blue-600 bg-blue-50 font-medium"
                                                        : "text-gray-600 hover:bg-gray-50"
                                                        }`}
                                                    onClick={() => setIsMobileMenuOpen(false)}>
                                                    {item.icon}
                                                    <span>{item.label}</span>
                                                </Link>
                                            )
                                        )}
                                    </nav>
                                </div>

                                {/* Fixed bottom section */}
                                <div className="p-4 border-t bg-white">
                                    <StoreLinks />
                                    <SocialIcons />
                                </div>
                            </motion.aside>
                        </>
                    )}
                </AnimatePresence>

                {/* Main Content - Independent Scroll, Mobile-Friendly */}
                <div className="flex-1 w-full lg:pl-64 flex flex-col">
                    <main className="flex-1 p-4 lg:p-8 overflow-y-auto w-full">
                        <div className="max-w-7xl mx-auto space-y-8 w-full">
                            {/* Welcome Section */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {t("professionalDashboard.welcomeBack", { firstName: user?.firstName })}
                                    </h1>
                                    <p className="text-gray-600 mt-1">
                                        {t("professionalDashboard.todayOverview")}
                                    </p>
                                </div>
                                <div className="lg:hidden">
                                    {/* <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsMobileMenuOpen(true)}
                  >
                    <Menu className="h-5 w-5 mr-2" />
                    Menu
                  </Button> */}
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
                                {stats.map((stat, index) => (
                                    <Card
                                        key={index}
                                        className={`${stat.bgColor} p-6 transition-all hover:shadow-lg w-full`}>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                                                {stat.icon}
                                            </div>
                                        </div>
                                        <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                                        <div className="mt-1 text-sm text-gray-600">{stat.title}</div>
                                    </Card>
                                ))}
                            </div>

                            {/* Orders Section */}
                            <Card className="overflow-hidden w-full">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b gap-4 w-full">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">{t("professionalDashboard.latestOrders")}</h2>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {t("professionalDashboard.trackManageOrders")}
                                        </p>
                                    </div>
                                    <Button 
                                        className="w-full sm:w-auto"
                                        onClick={() => navigate("/myorders")}
                                    >
                                        {t("professionalDashboard.viewAllOrders")}
                                    </Button>
                                </div>
                                <div className="overflow-x-auto w-full">
                                    <div className="inline-block min-w-full align-middle">
                                        <table className="min-w-full divide-y divide-gray-200 w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th
                                                        scope="col"
                                                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.orderId")}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.budget")}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.deliveryTime")}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.status")}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="hidden lg:table-cell px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.date")}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.action")}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {orders.length > 0 ? (
                                                    orders.slice(0, 5).map((order: any, index: number) => (
                                                        <tr key={order.id || index} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                #{order.id?.substring(0, 8) || "N/A"}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {getDynamicCurrencySymbol()}{order.amount?.toFixed(2) || "0.00"}
                                                            </td>
                                                            <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {calculateDeliveryTime(order.milestones)}
                                                            </td>
                                                            <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(Number(order.status?.id))}`}>
                                                                    {order.status?.name || "Unknown"}
                                                                </span>
                                                            </td>
                                                            <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {formatDate(order.createdAt)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm"
                                                                    onClick={() => navigate("/myorders")}
                                                                >
                                                                    {t("professionalDashboard.viewDetails")}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">
                                                            {t("professionalDashboard.noOrdersFound")}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </Card>

                            {/* Projects Section */}
                            <Card className="overflow-hidden w-full">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b gap-4 w-full">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">{t("professionalDashboard.activeProjects")}</h2>
                                        <p className="text-sm text-gray-600 mt-1">{t("professionalDashboard.ongoingProjectActivities")}</p>
                                    </div>
                                    <Button 
                                        className="w-full sm:w-auto"
                                        onClick={() => navigate("/myoffers")}
                                    >
                                        {t("professionalDashboard.viewAllProjects")}
                                    </Button>
                                </div>
                                <div className="overflow-x-auto w-full">
                                    <div className="inline-block min-w-full align-middle">
                                        <table className="min-w-full divide-y divide-gray-200 w-full">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th
                                                        scope="col"
                                                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.projectTitle")}
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {t("professionalDashboard.action")}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {projects.length > 0 ? (
                                                    projects.slice(0, 5).map((project: any, index: number) => (
                                                        <tr key={project.id || index} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-6 py-4">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {project.job?.title || t("professionalDashboard.na")}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {t("professionalDashboard.budget")} {getDynamicCurrencySymbol()}{project.amount?.toFixed(2) || "0.00"} • {formatDate(project.createdAt)}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm"
                                                                    onClick={() => navigate("/myoffers")}
                                                                >
                                                                    {t("professionalDashboard.viewOffer")}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={2} className="px-6 py-8 text-center text-sm text-gray-500">
                                                            {t("professionalDashboard.noProjectsFound")}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </Card>

                            {/* Performance Tier Section - Only show if monitoring data exists */}
                            {professionalStats?.monitoring && (
                                <Card className="p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                                <Award className="h-6 w-6 text-blue-600" />
                                                {t("professionalDashboard.performanceTier")}
                                            </h2>
                                            <p className="text-sm text-gray-600 mt-1">{t("professionalDashboard.currentPerformanceLevel")}</p>
                                        </div>
                                    </div>

                                    {/* Current Tier Display */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="h-8 w-8 text-blue-600" />
                                                    <div>
                                                        <p className="text-sm text-blue-600 font-medium">{t("professionalDashboard.currentTier")}</p>
                                                        <h3 className="text-2xl font-bold text-blue-900">
                                                            {professionalStats.monitoring.performanceTier}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-blue-700">
                                                <TrendingUp className="h-4 w-4" />
                                                <span>
                                                    {professionalStats.monitoring.isSuspended 
                                                        ? t("professionalDashboard.suspended")
                                                        : professionalStats.monitoring.totalWarnings > 0 
                                                        ? t("professionalDashboard.warnings", { count: professionalStats.monitoring.totalWarnings })
                                                        : t("professionalDashboard.goodStanding")}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <StarIcon className="h-8 w-8 text-green-600" />
                                                    <div>
                                                        <p className="text-sm text-green-600 font-medium">{t("professionalDashboard.trustScore")}</p>
                                                        <h3 className="text-2xl font-bold text-green-900">
                                                            {t("professionalDashboard.points", { score: professionalStats.monitoring.trustScore })}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-green-700">
                                                <CheckCircle className="h-4 w-4" />
                                                <span>
                                                    {professionalStats.monitoring.confirmedReports === 0 
                                                        ? t("professionalDashboard.noViolations")
                                                        : t("professionalDashboard.reports", { count: professionalStats.monitoring.confirmedReports })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tier Progress */}
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">{t("professionalDashboard.progressToNextTier")}</span>
                                            <span className="text-sm text-gray-600">
                                                {professionalStats.monitoring.tierScore} / 100
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div 
                                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                                                style={{ width: `${Math.min(professionalStats.monitoring.tierScore, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    {/* All Tiers */}
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("professionalDashboard.allPerformanceTiers")}</h3>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {[
                                                { name: t("professionalDashboard.basic"), color: "gray", icon: AlertCircle, score: "0-25" },
                                                { name: t("professionalDashboard.standard"), color: "blue", icon: CheckCircle, score: "26-50" },
                                                { name: t("professionalDashboard.advanced"), color: "green", icon: Shield, score: "51-75" },
                                                { name: t("professionalDashboard.elite"), color: "yellow", icon: Award, score: "76-100" }
                                            ].map((tier) => {
                                                const TierIcon = tier.icon;
                                                const isActive = professionalStats.monitoring.performanceTier === tier.name;
                                                const colorClasses = {
                                                    gray: isActive ? "bg-gray-100 border-gray-300 text-gray-900" : "bg-gray-50 border-gray-200 text-gray-600",
                                                    blue: isActive ? "bg-blue-100 border-blue-300 text-blue-900" : "bg-blue-50 border-blue-200 text-blue-600",
                                                    green: isActive ? "bg-green-100 border-green-300 text-green-900" : "bg-green-50 border-green-200 text-green-600",
                                                    yellow: isActive ? "bg-yellow-100 border-yellow-300 text-yellow-900" : "bg-yellow-50 border-yellow-200 text-yellow-600"
                                                };
                                                
                                                return (
                                                    <div 
                                                        key={tier.name}
                                                        className={`p-3 rounded-lg border-2 ${colorClasses[tier.color]} ${isActive ? "ring-2 ring-offset-2 ring-blue-500" : ""} transition-all`}
                                                    >
                                                        <TierIcon className={`h-5 w-5 mb-2 ${isActive ? "opacity-100" : "opacity-60"}`} />
                                                        <p className={`text-sm font-semibold ${isActive ? "opacity-100" : "opacity-75"}`}>{tier.name}</p>
                                                        <p className={`text-xs ${isActive ? "opacity-90" : "opacity-60"}`}>{tier.score} pts</p>
                                                        {isActive && (
                                                            <div className="mt-2 flex items-center gap-1 text-xs font-medium">
                                                                <CheckCircle className="h-3 w-3" />
                                                                <span>{t("professionalDashboard.current")}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Warning if suspended */}
                                    {professionalStats.monitoring.isSuspended && (
                                        <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                                <div className="text-sm text-red-900">
                                                    <p className="font-medium mb-1">{t("professionalDashboard.accountSuspended")}</p>
                                                    <p>{professionalStats.monitoring.suspensionReason}</p>
                                                    {professionalStats.monitoring.suspensionEndDate && (
                                                        <p className="mt-1">
                                                            {t("professionalDashboard.suspensionEnds", { date: new Date(professionalStats.monitoring.suspensionEndDate).toLocaleDateString() })}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Info Message */}
                                    {!professionalStats.monitoring.isSuspended && (
                                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <div className="text-sm text-blue-900">
                                                    <p className="font-medium mb-1">{t("professionalDashboard.howToImprove")}</p>
                                                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                                                        <li>{t("professionalDashboard.improveTip1")}</li>
                                                        <li>{t("professionalDashboard.improveTip2")}</li>
                                                        <li>{t("professionalDashboard.improveTip3")}</li>
                                                        <li>{t("professionalDashboard.improveTip4")}</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            )}
                        </div>
                    </main>

                    {/* Footer */}
                    <footer className="bg-white border-t w-full mt-auto">
                        <div className="max-w-7xl mx-auto">
                            {!isDataLoading && <SharedFooter3 />}
                        </div>
                    </footer>
                </div>
            </div>

            {/* Modals */}
            <PolicyModals
                showPrivacyModal={showPrivacyModal}
                showTermsModal={showTermsModal}
                showProfessionalTermsModal={showProfessionalTermsModal}
                onClosePrivacy={() => setShowPrivacyModal(false)}
                onCloseTerms={() => setShowTermsModal(false)}
                onCloseProfessionalTerms={() => setShowProfessionalTermsModal(false)}
            />
            
            {/* Warning Popup - Professional Monitoring */}
            {showWarning && currentWarning && (
                <WarningPopup
                    warning={currentWarning}
                    onDismiss={(id) => {
                        dispatch(dismissWarning(id));
                        // Check for next warning
                        if (pendingWarnings && pendingWarnings.length > 1) {
                            setCurrentWarning(pendingWarnings[1]);
                        } else {
                            setShowWarning(false);
                        }
                    }}
                    onClose={() => setShowWarning(false)}
                />
            )}
            
            {/* Transparent loading overlay */}
            {isDataLoading && <LoadingSpinner />}
        </div>
    );
};

export default FreelancerDashboard;
